import { computed, type ComputedRef } from 'vue';
import { QUANTITY_OPTIONS as BASE_QUANTITY_OPTIONS } from '@/shared/config';
import { ENTITY_CONFIGS, isValidEntityType } from '@/shared/lib';

interface RefLike<T> {
  value: T;
}

interface UseAdminListPageOptions {
  items: RefLike<unknown[]>;
  loading: RefLike<boolean>;
  searchTerm: RefLike<string>;
  metadata: RefLike<{ total?: number } | null | undefined>;
  selectedQuantity: RefLike<number | string>;
  isUsingFilters: ComputedRef<boolean>;
  onPageChanged: (page: number) => void;
  entityName: string;
}

export function useAdminListPage(options: UseAdminListPageOptions) {
  const {
    items,
    loading,
    searchTerm,
    metadata,
    selectedQuantity,
    isUsingFilters,
    onPageChanged,
    entityName,
  } = options;

  const entityConfig = isValidEntityType(entityName) ? ENTITY_CONFIGS[entityName] : undefined;
  const entityPlural = entityConfig?.plural ?? `${entityName}s`;
  const allLabel = entityConfig?.gender === 'female' ? 'Todas as' : 'Todos os';

  const QUANTITY_OPTIONS = BASE_QUANTITY_OPTIONS.map((option, index) =>
    index === BASE_QUANTITY_OPTIONS.length - 1
      ? { ...option, name: `${allLabel} ${entityPlural}` }
      : option
  );

  const totalItems = computed(() => metadata.value?.total ?? 0);

  const shouldShowPagination = computed(
    () =>
      totalItems.value > 0 &&
      typeof selectedQuantity.value === 'number' &&
      selectedQuantity.value < 100
  );

  const hasItems = computed(() => items.value.length > 0);

  const shouldShowNoResults = computed(
    () => !loading.value && !hasItems.value && searchTerm.value.trim() !== ''
  );

  const shouldShowNoFilterResults = computed(
    () =>
      !loading.value && !hasItems.value && searchTerm.value.trim() === '' && isUsingFilters.value
  );

  const shouldShowEmptyState = computed(
    () =>
      !loading.value && !hasItems.value && searchTerm.value.trim() === '' && !isUsingFilters.value
  );

  const shouldShowTotalOnly = computed(
    () => hasItems.value && !shouldShowPagination.value && !loading.value
  );

  const handlePageChange = (page: number) => {
    onPageChanged(page);
  };

  return {
    QUANTITY_OPTIONS,
    totalItems,
    shouldShowPagination,
    shouldShowTotalOnly,
    hasItems,
    shouldShowNoResults,
    shouldShowNoFilterResults,
    shouldShowEmptyState,
    handlePageChange,
    entityPlural,
  };
}
